'use client';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import { authorSlugToUrl } from '@/decodo/components/organisms/relatedArticles/utils';
import { formatDate } from '@/utils/formatDate';

import IconClockThree from '~/svg/IconClockThree.svg';

export type PostByLineProps = {
  releaseDate?: string;
  timeSpentReading?: number;
  author?: {
    image?: React.ReactNode;
    name?: string;
    authorPageSlug?: string;
  };
  disableImage?: boolean;
  classNames?: {
    releaseDate?: string;
    authorName?: string;
  };
};

export default function PostByLine({
  releaseDate,
  timeSpentReading,
  author,
  classNames,
  disableImage,
}: PostByLineProps) {
  const { texts } = useTranslation();

  const AuthorDetails = () =>
    !!author?.name ? (
      <div className='flex items-center	gap-2'>
        {!disableImage && (
          <div className='h-9 w-9 rounded-full'>{author.image}</div>
        )}
        <Text
          variant='body-m'
          color='gray-500'
          classNames={{
            wrapper: cn('dark:text-gray-500', classNames?.authorName),
          }}
        >
          {author.name}
        </Text>
      </div>
    ) : null;

  const AuthorContent = author?.authorPageSlug ? (
    <LinkButton
      variant='text'
      href={authorSlugToUrl(author.authorPageSlug)}
      noStyles
    >
      <AuthorDetails />
    </LinkButton>
  ) : (
    <AuthorDetails />
  );

  return (
    <div className='flex items-start justify-between sm:items-center'>
      <div className='flex flex-wrap items-center gap-3'>
        {AuthorContent}
        <div className='flex gap-4'>
          {releaseDate && (
            <Text
              variant='body-m'
              color='gray-500'
              classNames={{
                wrapper: cn('dark:text-gray-500', classNames?.releaseDate),
              }}
            >
              {formatDate(new Date(releaseDate))}
            </Text>
          )}
          {!!timeSpentReading && (
            <div className='flex gap-2 text-gray-500 dark:text-gray-500'>
              <IconClockThree />
              <Text variant='body-m' color='gray-500'>
                {timeSpentReading} {texts.blog.min_read}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
